import React, { useState, useEffect } from 'react';
import api, { enviarCodigoVerificacao, verificarCodigoVerificacao, cancelarInscricao } from '../api';
import toastr from 'toastr';
import { PDFDownloadLink } from '@react-pdf/renderer';
import ComprovantePDF from '../components/ComprovantePDF';
import { getInscricaoCancelamentoInfo } from '../utils/inscricoesUtils';

const statusLabel = (status) =>
  status === 'approved' ? 'Confirmado'
    : status === 'credenciado' ? 'Credenciado'
    : status === 'rejected' ? 'Cancelado'
    : status === 'cancelled' ? 'Cancelado'
    : status === 'refunded' ? 'Reembolsado'
    : 'Pendente';

const statusVariant = (status) =>
  status === 'approved' || status === 'credenciado' ? 'success'
    : status === 'rejected' || status === 'cancelled' || status === 'refunded' ? 'danger'
    : 'warning';

export default function MinhasInscricoes() {
  const [step, setStep] = useState('email'); // 'email' | 'codigo' | 'lista'
  const [email, setEmail] = useState('');
  const [codigo, setCodigo] = useState('');
  const [inscricoes, setInscricoes] = useState([]);
  const [loadingEnvio, setLoadingEnvio] = useState(false);
  const [loadingVerificacao, setLoadingVerificacao] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [cancelandoId, setCancelandoId] = useState(null);
  const [confirmCancelar, setConfirmCancelar] = useState(null); // { inscricao, info }

  const [selectedQrInsc, setSelectedQrInsc] = useState(null);
  const [selectedPixInfo, setSelectedPixInfo] = useState(null);
  const [loadingPix, setLoadingPix] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const handleEnviarCodigo = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoadingEnvio(true);
    try {
      const res = await enviarCodigoVerificacao(email);
      if (res.success) {
        toastr.success('Código enviado! Confira sua caixa de entrada.');
        setStep('codigo');
        setCooldown(45);
      } else {
        toastr.error(res.error || 'Erro ao enviar código.');
      }
    } catch (err) {
      toastr.error(err.response?.data?.error || 'Erro ao enviar código.');
    } finally {
      setLoadingEnvio(false);
    }
  };

  const handleVerificarCodigo = async (e) => {
    e.preventDefault();
    if (!codigo || codigo.length !== 6) return;
    setLoadingVerificacao(true);
    try {
      const res = await verificarCodigoVerificacao(email, codigo);
      if (res.success) {
        setInscricoes(res.inscricoes || []);
        setStep('lista');
      } else {
        toastr.error(res.error || 'Código inválido.');
      }
    } catch (err) {
      toastr.error(err.response?.data?.error || 'Código inválido.');
    } finally {
      setLoadingVerificacao(false);
    }
  };

  const handleTrocarEmail = () => {
    setStep('email');
    setCodigo('');
    setCooldown(0);
  };

  const handleFetchPix = async (pagamento_id) => {
    setLoadingPix(true);
    try {
      const res = await api.get(`/eventos/pagamento/${pagamento_id}`);
      if (res.data && res.data.success) {
        setSelectedPixInfo(res.data);
      } else {
        toastr.error('Erro ao recuperar dados do Pix. Tente novamente mais tarde.');
      }
    } catch (err) {
      console.error(err);
      toastr.error('Erro ao conectar ao servidor para buscar o Pix.');
    } finally {
      setLoadingPix(false);
    }
  };

  const abrirConfirmacaoCancelamento = (insc) => {
    const info = getInscricaoCancelamentoInfo(insc, { data_evento: insc.evento_data_evento });
    if (!info.canCancel) {
      toastr.warning(info.reason);
      return;
    }
    setConfirmCancelar({ inscricao: insc, info });
  };

  const confirmarCancelamento = async () => {
    if (!confirmCancelar) return;
    const { inscricao, info } = confirmCancelar;
    setCancelandoId(inscricao.id);
    try {
      const res = await cancelarInscricao(inscricao.id, info.refundAllowed);
      if (res.success) {
        toastr.success(info.refundAllowed ? 'Inscrição cancelada e valor estornado.' : 'Inscrição cancelada.');
        setInscricoes(prev => prev.map(i =>
          i.id === inscricao.id ? { ...i, status: info.refundAllowed ? 'refunded' : 'cancelled' } : i
        ));
      } else {
        toastr.error(res.error || 'Erro ao cancelar inscrição.');
      }
    } catch (err) {
      toastr.error(err.response?.data?.error || 'Erro ao cancelar inscrição.');
    } finally {
      setCancelandoId(null);
      setConfirmCancelar(null);
    }
  };

  const grupos = (() => {
    const map = new Map();
    inscricoes.forEach(insc => {
      if (!map.has(insc.evento_id)) {
        map.set(insc.evento_id, { evento_id: insc.evento_id, evento_titulo: insc.evento_titulo, itens: [] });
      }
      map.get(insc.evento_id).itens.push(insc);
    });
    return Array.from(map.values());
  })();

  return (
    <div className="container py-5" style={{ maxWidth: '760px' }}>
      <div className="text-center mb-4">
        <h1 className="fw-extrabold mb-1" style={{ fontWeight: 800, letterSpacing: '-0.5px' }}>Minhas Inscrições</h1>
        <p className="text-secondary mb-0" style={{ fontSize: '0.9rem' }}>
          Consulte, baixe seu comprovante ou cancele sua inscrição com segurança.
        </p>
      </div>

      <div
        style={{
          background: 'white',
          padding: '2rem',
          borderRadius: '16px',
          border: '1px solid var(--border)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
          marginBottom: '1.5rem'
        }}
      >
        {step === 'email' && (
          <form onSubmit={handleEnviarCodigo}>
            <div className="text-center mb-4">
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📧</div>
              <h5 className="fw-bold mb-1">Digite seu e-mail</h5>
              <p className="text-secondary mb-0" style={{ fontSize: '0.85rem' }}>
                Vamos enviar um código de verificação para confirmar que é você.
              </p>
            </div>
            <div className="d-flex flex-column flex-sm-row gap-2">
              <input
                type="email"
                className="form-control form-control-lg rounded-3 shadow-none border"
                placeholder="Seu endereço de e-mail"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
              />
              <button
                type="submit"
                className="btn btn-primary btn-lg fw-semibold rounded-3 text-nowrap"
                disabled={loadingEnvio}
                style={{ minWidth: '160px' }}
              >
                {loadingEnvio ? 'Enviando...' : 'Enviar Código'}
              </button>
            </div>
          </form>
        )}

        {step === 'codigo' && (
          <form onSubmit={handleVerificarCodigo}>
            <div className="text-center mb-4">
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🔐</div>
              <h5 className="fw-bold mb-1">Digite o código recebido</h5>
              <p className="text-secondary mb-0" style={{ fontSize: '0.85rem' }}>
                Enviamos um código de 6 dígitos para <strong>{email}</strong>.
              </p>
            </div>
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              className="form-control form-control-lg text-center rounded-3 shadow-none border mx-auto"
              placeholder="000000"
              value={codigo}
              onChange={e => setCodigo(e.target.value.replace(/\D/g, '').slice(0, 6))}
              style={{ maxWidth: '260px', fontSize: '1.6rem', fontWeight: 700, letterSpacing: '0.5rem' }}
              autoFocus
              required
            />
            <div className="d-flex justify-content-center mt-4">
              <button
                type="submit"
                className="btn btn-primary btn-lg fw-semibold rounded-3 px-5"
                disabled={loadingVerificacao || codigo.length !== 6}
              >
                {loadingVerificacao ? 'Verificando...' : 'Verificar e Acessar'}
              </button>
            </div>
            <div className="d-flex justify-content-center gap-3 mt-3" style={{ fontSize: '0.85rem' }}>
              <button
                type="button"
                className="btn btn-link p-0 text-secondary text-decoration-underline"
                onClick={handleTrocarEmail}
              >
                Trocar e-mail
              </button>
              <button
                type="button"
                className="btn btn-link p-0 text-decoration-underline"
                onClick={handleEnviarCodigo}
                disabled={cooldown > 0 || loadingEnvio}
              >
                {cooldown > 0 ? `Reenviar código (${cooldown}s)` : 'Reenviar código'}
              </button>
            </div>
          </form>
        )}

        {step === 'lista' && (
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div>
              <span className="text-secondary" style={{ fontSize: '0.85rem' }}>Consultando inscrições de</span>
              <div className="fw-bold">{email}</div>
            </div>
            <button className="btn btn-outline-secondary btn-sm rounded-3 fw-semibold" onClick={handleTrocarEmail}>
              Consultar outro e-mail
            </button>
          </div>
        )}
      </div>

      {step === 'lista' && (
        <div>
          {grupos.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              {grupos.map(grupo => (
                <div key={grupo.evento_id} style={{ background: 'white', padding: '1.5rem', borderRadius: '16px', border: '1px solid var(--border)', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
                  <h3 style={{ fontSize: '1.2rem', fontWeight: 700, marginBottom: '1rem', color: 'var(--text-primary)' }}>{grupo.evento_titulo}</h3>

                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    {grupo.itens.map((insc, idx) => {
                      const info = getInscricaoCancelamentoInfo(insc, { data_evento: insc.evento_data_evento });
                      return (
                        <div
                          key={insc.id}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: '0.75rem',
                            padding: '1rem 0',
                            borderBottom: idx === grupo.itens.length - 1 ? 'none' : '1px solid var(--border)'
                          }}
                        >
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', minWidth: '180px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                              <strong style={{ fontSize: '1rem' }}>{insc.lote_nome}</strong>
                              <span className={`badge badge-${statusVariant(insc.status)}`}>{statusLabel(insc.status)}</span>
                            </div>
                            <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
                              #INSC_{insc.id} · {Number(insc.valor_pago).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })} · Inscrito em {new Date(insc.data_inscricao).toLocaleDateString('pt-BR')}
                            </div>
                          </div>

                          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                            {(insc.status === 'approved' || insc.status === 'credenciado') && (
                              <>
                                <button
                                  className="btn btn-outline-secondary fw-semibold"
                                  style={{ padding: '0.5rem 1rem', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.4rem', borderRadius: '8px' }}
                                  onClick={() => setSelectedQrInsc(insc)}
                                >
                                  🎟️ QR Code
                                </button>
                                <PDFDownloadLink
                                  document={<ComprovantePDF inscricao={insc} />}
                                  fileName={`comprovante-${insc.id}.pdf`}
                                  className="btn btn-primary fw-semibold"
                                  style={{ padding: '0.5rem 1rem', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.4rem', textDecoration: 'none', borderRadius: '8px' }}
                                >
                                  {({ loading }) => loading ? '⏳ Gerando...' : '📄 Comprovante'}
                                </PDFDownloadLink>
                              </>
                            )}

                            {insc.status === 'pending' && insc.pagamento_id && (
                              <button
                                className="btn btn-warning fw-semibold text-dark shadow-sm"
                                style={{ padding: '0.5rem 1rem', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.4rem', borderRadius: '8px', border: 'none' }}
                                onClick={() => handleFetchPix(insc.pagamento_id)}
                                disabled={loadingPix}
                              >
                                {loadingPix ? '⏳ Buscando...' : '⚡ Pagar com PIX'}
                              </button>
                            )}

                            {info.canCancel && (
                              <button
                                className="btn btn-outline-danger fw-semibold"
                                style={{ padding: '0.5rem 1rem', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.4rem', borderRadius: '8px' }}
                                onClick={() => abrirConfirmacaoCancelamento(insc)}
                                disabled={cancelandoId === insc.id}
                              >
                                {cancelandoId === insc.id ? '⏳ Cancelando...' : '✕ Cancelar'}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '3rem 1rem', background: 'white', borderRadius: '16px', border: '1px solid var(--border)' }}>
              Nenhuma inscrição encontrada para este e-mail.
            </div>
          )}
        </div>
      )}

      {/* Modal de Confirmação de Cancelamento */}
      {confirmCancelar && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem'
        }} onClick={() => setConfirmCancelar(null)}>
          <div style={{
            background: '#ffffff', padding: '2rem', borderRadius: '16px', maxWidth: '400px', width: '100%',
            textAlign: 'center', boxShadow: '0 20px 40px rgba(0,0,0,0.12)', border: '1px solid var(--border)'
          }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>⚠️</div>
            <h5 className="fw-bold mb-2">Cancelar inscrição?</h5>
            <p className="text-secondary" style={{ fontSize: '0.9rem' }}>
              {confirmCancelar.info.refundAllowed
                ? `Sua inscrição em "${confirmCancelar.inscricao.lote_nome}" será cancelada e o valor de ${Number(confirmCancelar.inscricao.valor_pago || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })} será estornado.`
                : `Sua inscrição em "${confirmCancelar.inscricao.lote_nome}" será cancelada. Não há valor a estornar.`}
            </p>
            <div className="d-flex gap-2 mt-4">
              <button className="btn btn-outline-secondary w-50 rounded-3 fw-semibold" onClick={() => setConfirmCancelar(null)}>
                Voltar
              </button>
              <button className="btn btn-danger w-50 rounded-3 fw-semibold" onClick={confirmarCancelamento} disabled={cancelandoId !== null}>
                {cancelandoId !== null ? 'Cancelando...' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Exibição do QR Code Premium */}
      {selectedQrInsc && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(15, 23, 42, 0.65)',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }} onClick={() => setSelectedQrInsc(null)}>
          <div style={{
            background: '#ffffff',
            padding: '2.5rem 2rem',
            borderRadius: '16px',
            maxWidth: '380px',
            width: '100%',
            textAlign: 'center',
            boxShadow: '0 20px 40px rgba(0,0,0,0.12)',
            border: '1px solid var(--border)'
          }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: '1.4rem', fontWeight: 800, letterSpacing: '-0.5px', marginBottom: '0.25rem' }}>Ingresso Virtual</div>
            <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>Apresente na portaria para credenciamento rápido</div>

            <div style={{
              background: '#fff',
              border: '2px dashed var(--border)',
              padding: '1.5rem',
              borderRadius: '12px',
              display: 'inline-block',
              marginBottom: '1.5rem'
            }}>
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=INSC_${selectedQrInsc.id}`}
                alt="QR Code do Ingresso"
                style={{ display: 'block', width: '180px', height: '180px' }}
              />
            </div>

            <div style={{ textAlign: 'left', fontSize: '0.9rem', borderTop: '1px solid var(--border)', paddingTop: '1.25rem', marginBottom: '1.75rem' }}>
              <div style={{ marginBottom: '0.5rem', display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--text-secondary)' }}>Evento:</span>
                <strong>{selectedQrInsc.evento_titulo}</strong>
              </div>
              <div style={{ marginBottom: '0.5rem', display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--text-secondary)' }}>Participante:</span>
                <strong>{selectedQrInsc.nome}</strong>
              </div>
              <div style={{ marginBottom: '0.5rem', display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--text-secondary)' }}>Lote:</span>
                <strong>{selectedQrInsc.lote_nome}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--text-secondary)' }}>Status:</span>
                <span style={{ fontWeight: 700, color: 'var(--success)' }}>
                  {selectedQrInsc.status === 'credenciado' ? 'PRESENÇA CONFIRMADA' : 'INGRESSO ATIVO'}
                </span>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <PDFDownloadLink
                document={<ComprovantePDF inscricao={selectedQrInsc} />}
                fileName={`comprovante-${selectedQrInsc.id}.pdf`}
                className="btn btn-primary fw-semibold shadow-sm"
                style={{ width: '100%', padding: '0.8rem', fontSize: '0.9rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', textDecoration: 'none', borderRadius: '8px' }}
              >
                {({ loading }) => loading ? '⏳ Gerando Comprovante...' : '📄 Baixar Comprovante PDF'}
              </PDFDownloadLink>
              <button
                className="btn btn-outline-dark"
                style={{ width: '100%', padding: '0.8rem', fontWeight: 600, borderRadius: '8px' }}
                onClick={() => setSelectedQrInsc(null)}
              >
                Fechar Ingresso
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal do Pix Premium */}
      {selectedPixInfo && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(15, 23, 42, 0.65)',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }} onClick={() => { setSelectedPixInfo(null); setCopied(false); }}>
          <div style={{
            background: '#ffffff',
            padding: '2.5rem 2rem',
            borderRadius: '16px',
            maxWidth: '420px',
            width: '100%',
            textAlign: 'center',
            boxShadow: '0 20px 40px rgba(0,0,0,0.12)',
            border: '1px solid var(--border)'
          }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: '1.4rem', fontWeight: 800, letterSpacing: '-0.5px', marginBottom: '0.25rem' }}>Pagamento via PIX</div>
            <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>Efetue o pagamento para garantir a sua vaga</div>

            {selectedPixInfo.status === 'approved' ? (
              <div style={{ padding: '2rem 1rem', background: '#f0fdf4', borderRadius: '12px', border: '1px solid #bbf7d0', marginBottom: '1.5rem' }}>
                <span style={{ fontSize: '2.5rem', display: 'block', marginBottom: '0.5rem' }}>✅</span>
                <strong style={{ color: '#16a34a', display: 'block', fontSize: '1.1rem', marginBottom: '0.25rem' }}>Pagamento Confirmado!</strong>
                <span style={{ fontSize: '0.85rem', color: '#15803d' }}>
                  Sua inscrição já foi aprovada. Atualize a consulta para ver seu comprovante.
                </span>
              </div>
            ) : selectedPixInfo.status === 'cancelled' ? (
              <div style={{ padding: '2rem 1rem', background: '#fef2f2', borderRadius: '12px', border: '1px solid #fecaca', marginBottom: '1.5rem' }}>
                <span style={{ fontSize: '2.5rem', display: 'block', marginBottom: '0.5rem' }}>❌</span>
                <strong style={{ color: '#dc2626', display: 'block', fontSize: '1.1rem', marginBottom: '0.25rem' }}>Pagamento Expirado</strong>
                <span style={{ fontSize: '0.85rem', color: '#b91c1c' }}>
                  Este pagamento via Pix expirou. Por favor, faça uma nova inscrição no evento.
                </span>
              </div>
            ) : (
              <>
                <div style={{
                  background: '#fff',
                  border: '2px dashed var(--border)',
                  padding: '1.5rem',
                  borderRadius: '12px',
                  display: 'inline-block',
                  marginBottom: '1.5rem'
                }}>
                  {selectedPixInfo.qr_code_base64 ? (
                    <img
                      src={`data:image/png;base64,${selectedPixInfo.qr_code_base64}`}
                      alt="QR Code do Pix"
                      style={{ display: 'block', width: '180px', height: '180px' }}
                    />
                  ) : selectedPixInfo.qr_code ? (
                    <img
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(selectedPixInfo.qr_code)}`}
                      alt="QR Code do Pix"
                      style={{ display: 'block', width: '180px', height: '180px' }}
                    />
                  ) : (
                    <div style={{ width: '180px', height: '180px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)' }}>
                      QR Code indisponível
                    </div>
                  )}
                </div>

                <div style={{ textAlign: 'left', fontSize: '0.9rem', marginBottom: '1.5rem', background: 'var(--bg-primary)', padding: '1rem', borderRadius: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <span style={{ color: 'var(--text-secondary)' }}>Valor a Pagar:</span>
                    <strong>{Number(selectedPixInfo.transaction_amount).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--text-secondary)' }}>Método:</span>
                    <strong>Pix (Mercado Pago)</strong>
                  </div>
                </div>

                {selectedPixInfo.qr_code && (
                  <div style={{ marginBottom: '1.5rem', textAlign: 'left' }}>
                    <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '0.5rem', fontWeight: 600 }}>
                      Código Copia e Cola
                    </label>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <input
                        type="text"
                        readOnly
                        value={selectedPixInfo.qr_code}
                        style={{
                          flex: 1,
                          padding: '0.6rem 0.8rem',
                          fontSize: '0.8rem',
                          borderRadius: '8px',
                          border: '1px solid var(--border)',
                          background: '#f8fafc',
                          color: '#475569',
                          outline: 'none'
                        }}
                        onClick={e => e.target.select()}
                      />
                      <button
                        className={`btn ${copied ? 'btn-success' : 'btn-warning text-dark'}`}
                        style={{ padding: '0.6rem 1rem', fontSize: '0.85rem', fontWeight: 600, borderRadius: '8px', whiteSpace: 'nowrap' }}
                        onClick={() => {
                          navigator.clipboard.writeText(selectedPixInfo.qr_code);
                          setCopied(true);
                          toastr.success('Código Pix copiado!');
                          setTimeout(() => setCopied(false), 3000);
                        }}
                      >
                        {copied ? 'Copiado! ✓' : 'Copiar'}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <button
                className="btn btn-outline-dark"
                style={{ width: '100%', padding: '0.8rem', fontWeight: 600, borderRadius: '8px' }}
                onClick={() => { setSelectedPixInfo(null); setCopied(false); }}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
